import { OBJECTIVE_FIELDS } from "../../../constants";

const NOTE_INPUT_FIELDS = [
  "noteUuid",
  "patientUuid",
  "userUuid",
  "createdAt",
  "lastUpdatedAt",
  "taggedAt",
  "isDraft",
  "isTagged",
  "isApproved",
  "hasBeenExported",
  "shouldUseGenericObjective",
  "shouldUseCustomForm",
  "transcription",
  "audioStorageLink",
  "storageObjectName",
  "title",
  "quickNote",
  "subjective",
  "objective",
  "assessment",
  "plan",
  "soapTags",
  "objectiveFields",
  "customObjectiveFields",
  "excludedObjectiveFields",
  "customText",
  "additionalNotes",
  "keyHistory",
  "keyMedications",
  "vaccinationStatus",
  "clientSummary",
];

function structureDefaultObjectiveFields(
  data = {},
  shouldSaveAsGenericObjective = false,
  isNewNote = false,
) {
  if (isNewNote) {
    return;
  }

  const structuredObjectiveFields = {};

  data?.defaultObjectiveFieldArray?.forEach(({ name, value }) => {
    structuredObjectiveFields[name] = value;
  });
  structuredObjectiveFields.weight = data?.weight;

  data.objectiveFields = structuredObjectiveFields;

  data.shouldUseGenericObjective =
    shouldSaveAsGenericObjective === true;
}

function structureCustomObjectiveFields(
  data = {},
  isNewNote = false,
) {
  if (isNewNote) {
    return;
  }
  data.customObjectiveFields = data?.customObjectiveFieldArray?.map(
    (customObjectiveField) => {
      // Query-only fields.
      delete customObjectiveField["__typename"];
      delete customObjectiveField["symmetryLabels"];

      return customObjectiveField;
    },
  );
}

function removeUnneededFormFields(data) {
  Object.keys(data).forEach((name) => {
    if (!NOTE_INPUT_FIELDS.includes(name)) {
      delete data[name];
    }
  });
}

function calculateExcludedObjectiveFields({ data, isNewNote }) {
  const excludedObjectiveFields = [];

  if (isNewNote) {
    return excludedObjectiveFields;
  }

  const includedFields = [];
  data?.defaultObjectiveFieldArray?.forEach((field) => {
    includedFields.push(field.name);
  });

  OBJECTIVE_FIELDS.forEach((field) => {
    if (!includedFields.includes(field.name)) {
      excludedObjectiveFields.push(field.name);
    }
  });

  return excludedObjectiveFields;
}

/**
 * Feed me form data and I'll return a note input object.
 *
 * @param {object} data Form data from react-hook-form of a Basic note.
 * @param {boolean} shouldSaveAsGenericObjective boolean from the useBlank toggle.
 *
 * @returns {object} ready-to-use note input object for use with backend note/template mutations.
 */
export function formToNoteInput({
  data,
  shouldSaveAsGenericObjective = false,
  isNewNote = false,
}) {
  const formData = { ...data };

  structureDefaultObjectiveFields(
    formData,
    shouldSaveAsGenericObjective,
    isNewNote,
  );
  structureCustomObjectiveFields(formData, isNewNote);

  const excludedObjectiveFields = calculateExcludedObjectiveFields({
    data: formData,
    isNewNote,
  });

  removeUnneededFormFields(formData);

  return {
    ...formData,
    excludedObjectiveFields,
  };
}

/**
 * Formats a location JSON string into a standardized address format
 * @param {string} locationJson - JSON string containing location data
 * @returns {string} Formatted address
 */
export const formatAddress = (locationJson) => {
  if (!locationJson) return "Address not specified";
  try {
    // Convert Python-style single quotes to valid JSON double quotes
    const jsonString = locationJson
      .replace(/'/g, '"')
      .replace(/None/g, "null");

    const location = JSON.parse(jsonString);

    // Extract address components
    const { line1, line2, city, state, postal_code, country } =
      location;

    // Build address string from components
    const parts = [];

    if (line1) parts.push(line1);
    if (line2) parts.push(line2);
    if (city) parts.push(city);
    if (state) {
      if (postal_code) {
        parts.push(`${state} ${postal_code}`);
      } else {
        parts.push(state);
      }
    }
    if (country && country !== "US" && country !== "USA") {
      parts.push(country);
    }

    return parts.length > 0
      ? parts.join(", ")
      : "Address not specified";
  } catch (error) {
    console.warn("Error parsing location JSON:", error);
    if (typeof locationJson === "string") {
      return locationJson;
    }
    return "Invalid address format";
  }
};

/**
 * Examples:
 * formatAddress("{'line1':'137 Glasgow Street','city':'Kitchener','state':'ON','postal_code':'N2G 4X8','country':'CA'}")
 * // Returns: "137 Glasgow Street, Kitchener, ON N2G 4X8, CA"
 *
 * formatAddress("{'city':'Kitchener','state':'ON'}")
 * // Returns: "Kitchener, ON"
 *
 * formatAddress("{'line1':'137 Glasgow Street','line2':None,'city':'Kitchener','state':'ON','postal_code':'N2G 4X8','country':'CA'}")
 * // Returns: "137 Glasgow Street, Kitchener, ON N2G 4X8, CA"
 */
